import { useAuthenticator } from "@aws-amplify/ui-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"

import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import useClearSWR from "../../../../hooks/useClearSWR"
import { useAppDispatch } from "../../../../hooks/useDispatch"
import { setShowAccountSetUp } from "../../../../redux/preferencesSlice"
import { CLOSED } from "../../../../redux/types"
import { SIGN_OUT_SUCCESS } from "../../../../router/routes"
import { snackbar } from "../../../../utils/general/generalUtils"
import { deleteClientProtected } from "../../clients/clients"
import { REJECT_INVITE } from "../../urls/private"

interface IUseRejectInvites {
    isRejecting: boolean
    error: IAppError | undefined
    rejectInvite: () => Promise<void>
}

/**
 * Used to reject an invite to join an organisation
 * @returns {IUseRejectInvites} - loading and error states.
 */
const useRejectInvite = (): IUseRejectInvites => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { clearCache } = useClearSWR()
    const { signOut } = useAuthenticator((context) => [context.signOut])
    const [isRejecting, setIsRejecting] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)

    const rejectInvite = async (): Promise<void> => {
        try {
            setIsRejecting(true)
            await deleteClientProtected(REJECT_INVITE)

            setError(undefined)
            snackbar("Invite rejected", "success")

            // Clear cache and sign out user after successful invite rejection
            await clearCache()
            await signOut({ global: true })
            navigate(SIGN_OUT_SUCCESS)
            dispatch(setShowAccountSetUp(CLOSED))
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsRejecting(false)
        }
    }

    return {
        isRejecting,
        error,
        rejectInvite,
    }
}

export default useRejectInvite
